import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import clsx from 'clsx';
import { $createParagraphNode, $createTextNode, $getRoot, $isElementNode, $isTextNode } from 'lexical';
import React, { useEffect } from 'react';

import { preserveClassNamesAndStyles } from '~components/generic/editable-field/components/transformers/preserveClassNames';
import { ClipboardStylePreservationPlugin } from '~components/generic/editable-field/plugins/ClipboardStylePreservationPlugin';
import FloatingLinkEditorPlugin from '~components/generic/editable-field/plugins/FloatingLinkPlugin';
import { FloatingMenuPlugin } from '~components/generic/editable-field/plugins/FloatingMenuPlugin';
import { HeadingEnterPlugin } from '~components/generic/editable-field/plugins/HeadingEnterPlugin';
import { getEntityFieldDefinition } from '~services/entity-definitions';
import styles from '~components/generic/editable-field/EditableField/EditableField.css';

type ClassNameShape = string | { [key: string]: boolean };

interface LexicalEditorContentProps {
  onChangeEditor: (html: string) => void;
  hide?: boolean;
  entityType?: string;
  data?: string;
  editing?: boolean;
  fieldName?: string;
  targetRef?: React.RefObject<HTMLElement>;
  showAsHtmlCode: boolean;
  withoutDynamicTag?: boolean;
  includedControls?: string[];
  customClassName?: string;
  className?: ClassNameShape;
}

export default function LexicalEditorContent({
  onChangeEditor,
  entityType,
  data,
  editing,
  fieldName,
  showAsHtmlCode,
  withoutDynamicTag,
  includedControls,
  customClassName,
  hide,
  className,
}: LexicalEditorContentProps) {
  const [editor] = useLexicalComposerContext();

  function Placeholder() {
    return <div className="editor-placeholder">Enter some rich text...</div>;
  }

  const lastHtmlRef = React.useRef<string | null>(null);

  useEffect(() => {
    const removeUpdateListener = editor.registerUpdateListener(() => {
      editor.update(() => {
        let newValue: string;

        if (showAsHtmlCode) {
          const rootNode = $getRoot();
          const firstChild = rootNode.getFirstChild();
          newValue = firstChild?.getTextContent() || '';
        } else {
          newValue = $generateHtmlFromNodes(editor, null);
        }

        if (newValue === lastHtmlRef.current) {
          return;
        }

        lastHtmlRef.current = newValue;

        onChangeEditor(newValue);
      });
    });

    editor.update(() => {
      if (showAsHtmlCode) {
        // Create a pre node to maintain formatting
        const preNode = $createParagraphNode();
        const codeContent = $createTextNode(data || '');
        preNode.append(codeContent);

        $getRoot().clear();
        $getRoot().append(preNode);
      } else {
        const parser = new DOMParser();
        const isHTML = data?.includes('</') ?? false; // Safely check for HTML tags

        // Determine the tag to use based on the entity field definition
        const tagName =
          entityType && fieldName ? getEntityFieldDefinition(entityType, fieldName)?.tagName || 'div' : 'div';

        let contentToParse = '';

        if (!data) {
          contentToParse = withoutDynamicTag ? '<div></div>' : `<${tagName}></${tagName}>`;
        } else if (data?.includes('<p>') && fieldName === 'attributes.field_title.value') {
          contentToParse = `<${tagName}>${data}</${tagName}>`;
        } else if (typeof data === 'string' && !isHTML) {
          contentToParse =
            tagName === 'span'
              ? `<div>${data}</div>`
              : withoutDynamicTag
                ? `<div>${data}</div>`
                : `<${tagName}>${data}</${tagName}>`;
        } else if (isHTML) {
          contentToParse = data;
        }

        const dom = parser.parseFromString(contentToParse, 'text/html');
        const transformedDom = preserveClassNamesAndStyles(dom);
        const nodes = $generateNodesFromDOM(editor, transformedDom);

        const root = $getRoot();
        root.clear();

        // If only TextNodes were returned → wrap them in a Paragraph
        const normalizedNodes = nodes.map(node => {
          if ($isTextNode(node) || !$isElementNode(node)) {
            const paragraph = $createParagraphNode();
            paragraph.append(node);
            return paragraph;
          }
          return node;
        });

        root.append(...normalizedNodes);
      }
    });

    return () => {
      removeUpdateListener();
    };

    // Excluding 'data' from deps array to prevent re-renders on each text selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, withoutDynamicTag, entityType, fieldName]);

  const classes = clsx(
    {
      'lexical-editor': true,
      'c-module__field--editing': editing,
      [styles.base]: true,
      [styles.hide]: hide,
    },
    className,
  );

  return (
    <>
      <div className="lexical-editor-container">
        {!showAsHtmlCode && <FloatingMenuPlugin includedControls={includedControls} />}

        <div className={clsx('editor-inner', classes)}>
          <RichTextPlugin
            contentEditable={<ContentEditable className={`${customClassName} editor-input`} />}
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />

          <FloatingLinkEditorPlugin />

          <ListPlugin />
          <HeadingEnterPlugin />
          <ClipboardStylePreservationPlugin />
        </div>
      </div>
    </>
  );
}
